import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { closeFinvizScreenerModal } from '../store/slices/modalsSlice';
import { showGlobalMessage } from '../store/slices/globalMessageSlice';
import { updateDatabaseFiltersThunk, screenStocksThunk } from '../store/thunks';
import { FinvizScreenerDialog } from './FinvizScreenerDialog';
import type { FinvizFilterValues } from './FinvizScreenerDialog/types';
import type { FilterValues } from './ScreeningFiltersDialog/types';

/**
 * Transform flat dialog values to nested Redux/API shape.
 * Mirrors the logic from FiltersModal.tsx (transformDialogToRedux) without
 * modifying that file.
 */
const transformDialogToRedux = (
  dialogValues: FilterValues
): Record<string, any> => {
  const result: Record<string, any> = {
    active_category: dialogValues.active_category,
    active_preset_bundle: (dialogValues as any).active_preset_bundle ?? null,
    ma_timeframe_mode: dialogValues.ma_timeframe_mode,
    ma_filter_mode: dialogValues.ma_filter_mode,
    ma_filter_selection: dialogValues.ma_filter_selection,
    min_market_cap: dialogValues.min_market_cap,
    min_price_target_percent: dialogValues.min_price_target_percent,
    ma_proximity_min: dialogValues.ma_proximity_min,
    ma_proximity_max: dialogValues.ma_proximity_max,
    require_recent_ma150_cross:
      (dialogValues as any).require_recent_ma150_cross !== undefined
        ? Boolean((dialogValues as any).require_recent_ma150_cross)
        : true,
    recent_ma150_cross_lookback_days:
      (dialogValues as any).recent_ma150_cross_lookback_days !== undefined
        ? Number((dialogValues as any).recent_ma150_cross_lookback_days)
        : 6,
    recent_ma150_cross_bypass_pct:
      (dialogValues as any).recent_ma150_cross_bypass_pct !== undefined
        ? Number((dialogValues as any).recent_ma150_cross_bypass_pct)
        : 0.02,
    sh_avgvol: dialogValues.sh_avgvol ?? '',
    sh_relvol: dialogValues.sh_relvol ?? '',
    sh_curvol: dialogValues.sh_curvol ?? '',
    trend_period_months: dialogValues.trend_period_months,
    price_target_mode: dialogValues.price_target_mode,
    standard_screening_enabled: dialogValues.standard_screening_enabled,
    chart_analysis_enabled: dialogValues.chart_analysis_enabled,
    instrument_type_filter: dialogValues.instrument_type_filter,
    trend_direction: dialogValues.trend_direction || 'any',
    trend_strength: dialogValues.trend_strength || 'any',
    momentum_confirmation: dialogValues.momentum_confirmation || 'any',
    doji_filter_enabled: dialogValues.doji_filter_enabled,
    cup_handle_filter_enabled: dialogValues.cup_handle_filter_enabled,
    head_shoulders_filter_enabled: dialogValues.head_shoulders_filter_enabled,
    double_pattern_filter_enabled: dialogValues.double_pattern_filter_enabled,
    breakout_filter_enabled: dialogValues.breakout_filter_enabled,
    strong_red_reversal_filter_enabled:
      dialogValues.strong_red_reversal_filter_enabled,
    green_doji_filter_enabled: dialogValues.green_doji_filter_enabled,
    pullback_to_support_filter_enabled:
      dialogValues.pullback_to_support_filter_enabled,
    gap_down_filter_enabled: dialogValues.gap_down_filter_enabled,
    goldenCrossFilterEnabled: dialogValues.goldenCrossFilterEnabled,
    fib_golden_zone_filter_enabled: dialogValues.fib_golden_zone_filter_enabled,
    volume_structure_enabled: dialogValues.volume_structure_enabled,
    darvas_box_filter_enabled: dialogValues.darvas_box_filter_enabled,
    strong_support_filter_enabled: dialogValues.strong_support_filter_enabled,
    volume_spike_enabled: dialogValues.volume_spike_enabled,
    price_action_enabled: dialogValues.price_action_enabled,
    breakout_enabled: dialogValues.breakout_enabled,
    rsi_recovery_enabled: dialogValues.rsi_recovery_enabled,
    stochastic_enabled: dialogValues.stochastic_enabled,
    ma_bounce_reversal_enabled: dialogValues.ma_bounce_reversal_enabled,
    ma150_v_reversal_enabled: dialogValues.ma150_v_reversal_enabled,
    support_retest_enabled: dialogValues.support_retest_enabled,
    strong_support_strategy: dialogValues.strong_support_strategy,
    volume_spike_strategy: dialogValues.volume_spike_strategy,
    price_action_strategy: dialogValues.price_action_strategy,
    breakout_strategy: dialogValues.breakout_strategy,
    rsi_recovery_strategy: dialogValues.rsi_recovery_strategy,
    stochastic_strategy: dialogValues.stochastic_strategy,
    ma_bounce_reversal_strategy: dialogValues.ma_bounce_reversal_strategy,
    ma150_v_reversal_strategy: dialogValues.ma150_v_reversal_strategy,
    support_retest_strategy: dialogValues.support_retest_strategy,
    bullish_momentum_enabled: false,
    bullish_transition_enabled: false,
    bearish_transition_enabled: false,
  };

  // Persist finviz_* keys
  Object.entries(dialogValues).forEach(([key, value]) => {
    if (key.startsWith('finviz_')) {
      result[key] = value;
    }
  });

  // Transform index_filters array to object
  if (Array.isArray(dialogValues.index_filters)) {
    const idx = dialogValues.index_filters;
    result.index_filters = {
      sp500_enabled: idx.includes('sp500'),
      nasdaq100_enabled: idx.includes('nasdaq100'),
      russell2000_enabled: idx.includes('russell2000'),
      djia_enabled: idx.includes('djia'),
    };
  }

  // Build nested config objects from flat keys
  const nestedConfigs: Record<string, Record<string, any>> = {};
  Object.entries(dialogValues).forEach(([key, value]) => {
    const dotIdx = key.indexOf('.');
    if (dotIdx > 0) {
      const configKey = key.substring(0, dotIdx);
      const field = key.substring(dotIdx + 1);
      if (!nestedConfigs[configKey]) nestedConfigs[configKey] = {};
      nestedConfigs[configKey][field] = value;
    }
  });
  Object.entries(nestedConfigs).forEach(([configKey, fields]) => {
    result[configKey] = fields;
  });

  return result;
};

/**
 * Transform Redux nested state to flat shape for the screening filters section.
 * Mirrors the logic from FiltersModal.tsx (transformReduxToDialog).
 */
const transformReduxToDialogForScreening = (
  reduxFilters: Record<string, any>
): FilterValues => {
  const result: FilterValues = { ...reduxFilters };

  if (
    reduxFilters.index_filters &&
    typeof reduxFilters.index_filters === 'object'
  ) {
    const indexArr: string[] = [];
    const idx = reduxFilters.index_filters;
    if (idx.sp500_enabled) indexArr.push('sp500');
    if (idx.nasdaq100_enabled) indexArr.push('nasdaq100');
    if (idx.russell2000_enabled) indexArr.push('russell2000');
    if (idx.djia_enabled) indexArr.push('djia');
    result.index_filters = indexArr;
  }

  const nestedConfigs = [
    'strong_ma_support_config',
    'fib_golden_zone_config',
    'darvas_box_config',
    'strong_support_config',
    'volume_spike_config',
    'price_action_config',
    'breakout_config',
    'rsi_recovery_config',
    'ma_bounce_reversal_config',
    'ma150_v_reversal_config',
    'support_retest_config',
    'gap_down_filter',
  ];
  nestedConfigs.forEach((configKey) => {
    if (
      reduxFilters[configKey] &&
      typeof reduxFilters[configKey] === 'object'
    ) {
      Object.entries(reduxFilters[configKey]).forEach(([k, v]) => {
        result[`${configKey}.${k}`] = v;
      });
    }
  });

  result.bullish_momentum_enabled = Boolean(
    reduxFilters.bullish_momentum_enabled
  );

  Object.entries(reduxFilters).forEach(([key, value]) => {
    if (key.startsWith('finviz_')) {
      result[key] = value;
    }
  });

  return result;
};

const FinvizScreenerModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useSelector(
    (state: RootState) => state.modals.finvizScreenerModal
  );
  const reduxFilters = useSelector((state: RootState) => state.filters);

  // Transform Redux state for screening filters section.
  // Depend on reduxFilters so we don't read stale state when the dialog opens
  // before the initial /api/filters/database fetch has populated Redux.
  // useFilterState's hasSyncedInitial flag guards against overwriting user edits.
  const screeningInitialValues = useMemo(
    () => transformReduxToDialogForScreening(reduxFilters),
    [reduxFilters]
  );

  // Extract persisted finviz_* keys for universe tab hydration.
  // Same rationale as above - read from current Redux state, not a stale snapshot.
  const universeInitialValues = useMemo(() => {
    const vals: Record<string, any> = {};
    Object.entries(reduxFilters).forEach(([key, value]) => {
      if (key.startsWith('finviz_') && value != null) {
        vals[key] = value;
      }
    });
    return Object.keys(vals).length > 0 ? vals : undefined;
  }, [reduxFilters]);

  const handleClose = useCallback(() => {
    dispatch(closeFinvizScreenerModal());
  }, [dispatch]);

  const handleApply = useCallback(
    async (
      tickers: string[],
      _filters: FinvizFilterValues,
      screeningFilters?: FilterValues,
      startScreening?: boolean
    ) => {
      // 1. Save screening filter config + universe selections to DB
      try {
        const transformedValues = screeningFilters
          ? transformDialogToRedux(screeningFilters)
          : {};
        // Mark that we want to use finviz cache for screening
        transformedValues.use_finviz_cache = true;
        // Merge universe finviz_* values so they persist per user
        if (_filters) {
          Object.entries(_filters).forEach(([key, value]) => {
            if (value != null) {
              transformedValues[key] = value;
            }
          });
        }
        await dispatch(updateDatabaseFiltersThunk(transformedValues)).unwrap();
      } catch (error) {
        console.error('Failed to save filters:', error);
      }

      // 2. Show the "no matching stocks" warning ONLY for a pure save (not
      // Save & Screen). For pattern-filter screening the finviz universe is
      // often empty by design - the screen runs against the full DB universe -
      // so the warning is misleading when startScreening is true (A2).
      if (tickers.length === 0 && !startScreening) {
        dispatch(
          showGlobalMessage({
            message:
              'Filters saved but no matching stocks found. Adjust your criteria and try again.',
            type: 'warning',
          })
        );
      }

      // 3. Auto-trigger screening whenever requested. An empty finviz_tickers
      // array makes the backend screen the FULL DB universe (it only applies
      // the finviz filter when the list is non-empty - app_db_new.py:1842), so
      // pattern-only scans must NOT be gated on tickers.length > 0 (A2: this
      // was the "Save & Screen doesn't screen" bug).
      if (startScreening) {
        try {
          await dispatch(
            screenStocksThunk({ finviz_tickers: tickers })
          ).unwrap();
        } catch (error) {
          console.error('Failed to start screening:', error);
        }
      }
    },
    [dispatch]
  );

  if (!isOpen) return null;

  return (
    <FinvizScreenerDialog
      open={isOpen}
      onClose={handleClose}
      onApply={handleApply}
      screeningInitialValues={screeningInitialValues}
      universeInitialValues={universeInitialValues}
    />
  );
};

export default FinvizScreenerModal;
